// React and related imports
import React, { useCallback, useState } from 'react';

// Flowbite components
import { Accordion } from 'flowbite-react';

// Custom Components
import { ContentTable } from '../ContentTable/ContentTable';
import { GroupedContentAccordionHeader } from './GroupedContentAccordionHeader';
import { CreateNewContentButton } from './CreateNewContentButton';

// Types & Interfaces
import { IContentGroupedByContextResponse } from '../../common/interfaces/ContentGroupedByContextResponse.interface';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Redux hooks and actions
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import {
  openContentModal,
  setSelectedContent,
} from '../../redux/slices/contentSlice';

// Constants
import { INITIAL_CONTENT_STATE } from '../../common/constants/initialContentState';

// Utilis
import { getLocalizedValueFromMultiLangValues } from '../../utils/getLocalizedValueFromMultiLangValues';

interface GroupedContentAccordionProps {
  groupedContents: IContentGroupedByContextResponse;
  index: number;
}

export const GroupedContentAccordion: React.FC<
  GroupedContentAccordionProps
> = ({ groupedContents, index }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [openIndexes, setOpenIndexes] = useState<number[]>([0]);
  const isOpen = openIndexes.includes(index);

  const handleAccordionToggle = (index: number) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleCreateNewContent = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      if (groupedContents.contextDetails.id) {
        dispatch(
          setSelectedContent({
            ...INITIAL_CONTENT_STATE,
            context: groupedContents.contextDetails.id,
            contextType: groupedContents.contextDetails.type,
          })
        );
        dispatch(openContentModal({ isModalOpenedForAdd: true }));
      }
    },
    [dispatch]
  );

  return (
    <Accordion
      key={groupedContents._id}
      className="mb-5 border-primary-100"
      collapseAll={index !== 0}
      onClick={() => handleAccordionToggle(index)}
    >
      <Accordion.Panel>
        <Accordion.Title
          className={`${isOpen ? 'accordion-opened' : 'accordion'}`}
        >
          <GroupedContentAccordionHeader
            title={getLocalizedValueFromMultiLangValues(
              groupedContents.contextDetails.title
            )}
            contentContextType={groupedContents.contextDetails.type}
            status={groupedContents.contextDetails.status}
            contentsNumber={groupedContents.totalContents || 0}
          />
          {groupedContents.contents?.length > 0 && (
            <CreateNewContentButton
              onClick={handleCreateNewContent}
              label={t('content.addNewContent')}
            />
          )}
        </Accordion.Title>

        <Accordion.Content className="bg-transparent p-4 dark:bg-transparent">
          {groupedContents.contents?.length > 0 ? (
            <ContentTable
              contents={groupedContents.contents}
              contextType={groupedContents.contextDetails.type}
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-3">
              <p className="p-4 text-center dark:text-gray-500">
                {t('content.noContentAvailable')}
              </p>
              <div className="w-1/3 pb-2">
                <CreateNewContentButton
                  onClick={handleCreateNewContent}
                  label={t('content.addNewContent')}
                />
              </div>
            </div>
          )}
        </Accordion.Content>
      </Accordion.Panel>
    </Accordion>
  );
};
